import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const TEAL = [14, 74, 85]; // hsl(192 72% 21%)
const SAND = [216, 174, 121];

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export async function generateDoctorReport(snapshot) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 40;
  let y = margin;

  // Header
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 90, "F");
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("GuardianNest", margin, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("Doctor Report", margin, 70);
  doc.setFontSize(9);
  doc.setTextColor(220);
  doc.text(`Generated ${new Date(snapshot.generated_at).toLocaleString()}`, doc.internal.pageSize.getWidth() - margin, 70, { align: "right" });
  y = 120;

  // Profile block
  const p = snapshot.profile || {};
  doc.setTextColor(20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(p.name || "Profile", margin, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const meta = [
    p.date_of_birth ? `DOB: ${p.date_of_birth}` : null,
    p.sex ? `Sex: ${p.sex}` : null,
    p.blood_type ? `Blood: ${p.blood_type}` : null,
    p.relationship ? `Relationship: ${p.relationship}` : null,
  ].filter(Boolean);
  doc.text(meta.join("   ·   "), margin, y);
  y += 18;

  if (p.allergies?.length) {
    doc.setFont("helvetica", "bold");
    doc.text("Allergies:", margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(p.allergies.join(", "), margin + 60, y);
    y += 18;
  }
  if (p.notes) {
    doc.setFont("helvetica", "italic");
    doc.text(doc.splitTextToSize(p.notes, doc.internal.pageSize.getWidth() - margin * 2), margin, y);
    y += 18;
  }

  y += 6;

  // Section helper
  const section = (title, head, rows) => {
    if (!rows || rows.length === 0) return;
    autoTable(doc, {
      startY: y,
      head: [head],
      body: rows,
      theme: "striped",
      headStyles: { fillColor: TEAL, textColor: 255, fontStyle: "bold" },
      styles: { fontSize: 9, cellPadding: 5 },
      margin: { left: margin, right: margin },
      didDrawPage: () => {},
      didParseCell: () => {},
      willDrawCell: () => {},
      didDrawCell: () => {},
      pageBreak: "auto",
      tableLineColor: SAND,
      didDrawPage: (data) => {
        // header band on page breaks
      },
    });
    const last = doc.lastAutoTable.finalY;
    // Section title bar
    doc.setFillColor(...SAND);
    doc.rect(margin, last + 4, 4, 14, "F");
    doc.setTextColor(20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    y = last + 30;
  };

  const sectionTitle = (title) => {
    if (y > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      y = margin;
    }
    doc.setFillColor(...TEAL);
    doc.rect(margin, y - 12, 4, 14, "F");
    doc.setTextColor(20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(title, margin + 10, y);
    y += 10;
  };

  // Medications
  sectionTitle("Medications");
  autoTable(doc, {
    startY: y,
    head: [["Medication", "Dosage", "Frequency", "Active", "Instructions"]],
    body: (snapshot.medications || []).map((m) => [
      m.name,
      m.dosage || "—",
      m.frequency || "—",
      m.is_active ? "Yes" : "No",
      m.instructions || "—",
    ]),
    theme: "striped",
    headStyles: { fillColor: TEAL, textColor: 255 },
    styles: { fontSize: 9, cellPadding: 5 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 20;

  // Vaccinations
  sectionTitle("Vaccinations");
  autoTable(doc, {
    startY: y,
    head: [["Vaccine", "Date given", "Dose #", "By", "Next due"]],
    body: (snapshot.vaccinations || []).map((v) => [
      v.vaccine_name,
      v.date_administered || "—",
      v.dose_number || "—",
      v.administered_by || "—",
      v.next_due || "—",
    ]),
    theme: "striped",
    headStyles: { fillColor: TEAL, textColor: 255 },
    styles: { fontSize: 9, cellPadding: 5 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 20;

  // Growth
  sectionTitle("Growth measurements");
  autoTable(doc, {
    startY: y,
    head: [["Date", "Height (cm)", "Weight (kg)", "Head (cm)", "Notes"]],
    body: (snapshot.growth || []).map((g) => [
      g.measured_at,
      g.height_cm ?? "—",
      g.weight_kg ?? "—",
      g.head_circumference_cm ?? "—",
      g.notes || "—",
    ]),
    theme: "striped",
    headStyles: { fillColor: TEAL, textColor: 255 },
    styles: { fontSize: 9, cellPadding: 5 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 20;

  // Appointments
  sectionTitle("Appointments");
  autoTable(doc, {
    startY: y,
    head: [["When", "Title", "Provider", "Status", "Notes"]],
    body: (snapshot.appointments || []).map((a) => [
      fmtDate(a.starts_at),
      a.title,
      a.provider || "—",
      a.status,
      a.notes || "—",
    ]),
    theme: "striped",
    headStyles: { fillColor: TEAL, textColor: 255 },
    styles: { fontSize: 9, cellPadding: 5 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 20;

  // Insurance
  if (snapshot.insurance?.length) {
    sectionTitle("Insurance");
    autoTable(doc, {
      startY: y,
      head: [["Provider", "Plan", "Member ID", "Policy", "Phone"]],
      body: snapshot.insurance.map((i) => [
        i.provider,
        i.plan_name || "—",
        i.member_id || "—",
        i.policy_number || "—",
        i.phone || "—",
      ]),
      theme: "striped",
      headStyles: { fillColor: TEAL, textColor: 255 },
      styles: { fontSize: 9, cellPadding: 5 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // Contacts
  if (snapshot.contacts?.length) {
    sectionTitle("Contacts");
    autoTable(doc, {
      startY: y,
      head: [["Name", "Role", "Phone", "Email"]],
      body: snapshot.contacts.map((c) => [
        c.name,
        c.role,
        c.phone || "—",
        c.email || "—",
      ]),
      theme: "striped",
      headStyles: { fillColor: TEAL, textColor: 255 },
      styles: { fontSize: 9, cellPadding: 5 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 20;
  }

  // Sick episodes summary
  if (snapshot.sick_episodes?.length) {
    sectionTitle("Recent illness episodes");
    autoTable(doc, {
      startY: y,
      head: [["Started", "Title", "Status", "Notes"]],
      body: snapshot.sick_episodes.slice(0, 10).map((e) => [
        fmtDate(e.started_at),
        e.title,
        e.status,
        e.notes || "—",
      ]),
      theme: "striped",
      headStyles: { fillColor: TEAL, textColor: 255 },
      styles: { fontSize: 9, cellPadding: 5 },
      margin: { left: margin, right: margin },
    });
  }

  // Footer
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      `GuardianNest · Doctor Report for ${p.name || "—"} · Page ${i} of ${pages}`,
      margin,
      doc.internal.pageSize.getHeight() - 20
    );
  }

  const safeName = (p.name || "profile").replace(/\W+/g, "_");
  doc.save(`GuardianNest_DoctorReport_${safeName}.pdf`);
}
